import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { FlexView, Text } from '@cuemath/comps';
import { openModal, closeModal } from 'actions/modal';
import { Grid } from 'semantic-ui-react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import COLORS from 'constants/colors';

import { MODAL_FORM, FORM_ACTIONS } from 'constants/form';
import {
  STUDENT_DEMOGRAPHICS_DETAILS_LABELS,
  CONTACT_DETAILS_LABELS,
  ACTIONS_LABELS,
  FIELD_TYPE,
  IDENTIFIERS_LABELS,
  IDENTIFIER_VALUES,
  STUDENT_TYPE,
} from 'constants/student-profile';
import { USER_SELECT_DISABLE_ARRAY } from 'constants/constants';

const Heading = styled(Text)`
  background-color: ${COLORS.HAWKESBLUE};
  border: 1px solid ${COLORS.SCIENCEBLUE};
  text-align: center;
  padding: 6px;
  margin: 20px 10px 0 10px;
  border-top-left-radius: 5px;
  border-top-right-radius: 5px;
`;

const InfoWrapper = styled.div`
  border: 1px solid ${COLORS.SILVERCHALICE};
  border-top-color: transparent;
  margin: 0 10px;
  padding: 10px 10px 30px 10px;
`;

const LabelWrapper = styled(Text)`
  margin: 5px 0;
`;

const ValueWrapper = styled(Text)`
  border-bottom: 1px solid black;
  word-break: break-all;
  user-select: ${({ disableUserSelect }) => (disableUserSelect ? 'none' : 'auto')};
`;

const LinkWrapper = styled(Text)`
  color: ${COLORS.BOSTON_BLUE};
  text-decoration: none;
  cursor: pointer;
`;

const LinkNav = styled(Link)`
  margin-top: 10px;
  text-decoration: none;
  cursor: pointer;
  color: blue;
  text-align: center;
  &:hover {
    color: blue;
  }
  &:active {
    color: blue;
  }
`;

const DetailsContainer = (label = '', value = '') => {
  return (
    <FlexView>
      <LabelWrapper weight="bold">{label}</LabelWrapper>
      <ValueWrapper
        disableUserSelect={USER_SELECT_DISABLE_ARRAY.includes(label.toLowerCase())}
      >
        {value}
      </ValueWrapper>
    </FlexView>
  );
};

const FieldContainer = (label = '', value = '', width = 8) => {
  return <Grid.Column width={width}>{DetailsContainer(label, value)}</Grid.Column>;
};

const LinkContainer = (label = '', value = () => {}, width = 8) => {
  return (
    <Grid.Column width={width}>
      <LinkWrapper onClick={value}>{label}</LinkWrapper>
    </Grid.Column>
  );
};
class StudentProfile extends PureComponent {
  static propTypes = {
    onUpdate: PropTypes.func.isRequired,
    student: PropTypes.shape({}).isRequired,
    actions: PropTypes.shape({
      openModal: PropTypes.func.isRequired,
    }).isRequired,
    studentProfileInfo: PropTypes.shape({}).isRequired,
  };

  openEditModel = (modalName, blocking = false) => {
    const { actions, onUpdate, studentProfileInfo: student } = this.props;

    actions.openModal(
      modalName,
      {
        size: 'custom',
        width: `600px`,
        height: '400px',
        blocking,
        overflow: 'visible',
      },
      { onUpdate, student },
    );
  };

  openEditCredsModal = modalName => {
    const {
      actions,
      onUpdate,
      studentProfileInfo: { parent_service_id: parentId, id: studentId },
    } = this.props;
    actions.openModal(
      modalName,
      {
        size: 'custom',
        width: `${Math.ceil((window.innerWidth * 60) / 100)}px`,
        height: '350px',
      },
      { data: { parentId, studentId, onUpdate } },
    );
  };

  openStudentForm = ({ formType, data }) => {
    const { actions } = this.props;
    if (FORM_ACTIONS.hasOwnProperty(formType)) {
      actions.openModal(
        MODAL_FORM.STUDENT,
        {
          size: 'custom',
          width: `${Math.ceil((window.innerWidth * 60) / 100)}px`,
          height: '90vh',
        },
        { formType, data },
      );
    }
  };

  renderStudentProfileAccordion = (
    heading = '',
    fields = [],
    type = '',
    renderer = () => {},
  ) => {
    return (
      <FlexView>
        <Heading>{heading}</Heading>
        <InfoWrapper>
          <Grid>{renderer(fields, type)}</Grid>
        </InfoWrapper>
      </FlexView>
    );
  };

  renderColumnContainersInGrid = (fields = [], type) => {
    return fields.map((row = [], index) => (
      <Grid.Row key={index}>
        {row.map(({ label, value, width }) => {
          return (
            <React.Fragment key={label}>
              {type === FIELD_TYPE.FIELD && <>{FieldContainer(label, value, width)}</>}
              {type === FIELD_TYPE.LINK && <>{LinkContainer(label, value, width)}</>}
            </React.Fragment>
          );
        })}
      </Grid.Row>
    ));
  };

  renderLinkOnlyColumn = fields => {
    const [studentPIILink] = fields;

    return (
      <div>
        <LinkWrapper>
          <LinkNav
            target="_blank"
            to={{
              pathname: `/student-pii/${studentPIILink.studentId}`,
            }}
          >
            {studentPIILink.label}
          </LinkNav>
        </LinkWrapper>
      </div>
    );
  };

  renderStudentDemograpicDetails = studentProfileInfo => {
    const {
      derived_region_text: derivedRegion,
      first_name: firstName,
      grade,
      id,
      source,
      last_name: lastName,
      parent_name: parentName,
      parent_data: parentData,
      student_service_id: studentServiceId = '',
      state = '',
    } = studentProfileInfo;

    const { rm_data: rmManager = {}, ref_code: refCode } = parentData || {};
    const { first_name: TMFirstName = '', last_name: TMLastName = '' } = rmManager || {};
    const {
      HEADING,
      FIRST_NAME,
      LAST_NAME,
      GRADE,
      PARENT_NAME,
      RM_NAME,
      ASSIGNED_REGION,
      ID,
      STATE,
      REFERRAL_LINK,
      STUDENT_SERVICE_ID,
      SOURCE,
    } = STUDENT_DEMOGRAPHICS_DETAILS_LABELS;
    const referralLink = refCode
      ? `https://www.cuemath.com/gift/referral/${refCode}/?utm_source=la`
      : '';

    const fieldsData = [
      [
        {
          label: FIRST_NAME,
          value: firstName,
          width: 8,
        },
        {
          label: LAST_NAME,
          value: lastName,
          width: 8,
        },
      ],
      [{ label: GRADE, value: grade, width: 8 }],
      [
        {
          label: PARENT_NAME,
          value: parentName,
          width: 8,
        },
        {
          label: RM_NAME,
          value: `${TMFirstName} ${TMLastName}`,
          width: 8,
        },
      ],
      [
        {
          label: ASSIGNED_REGION,
          value: derivedRegion,
          width: 16,
        },
      ],
      [{ label: REFERRAL_LINK, value: referralLink, width: 16 }],
      [{ label: ID, value: id, width: 16 }],
      [{ label: SOURCE, value: source, width: 16 }],
      [{ label: STATE, value: state, width: 16 }],
      [
        {
          label: STUDENT_SERVICE_ID,
          value: studentServiceId,
          width: 16,
        },
      ],
    ];

    return this.renderStudentProfileAccordion(
      HEADING,
      fieldsData,
      FIELD_TYPE.FIELD,
      this.renderColumnContainersInGrid,
    );
  };

  renderContactDetails = studentProfileInfo => {
    const { student_id: studentId } = studentProfileInfo;
    const { HEADING } = CONTACT_DETAILS_LABELS || {};

    const fieldsData = [
      { label: 'Click here to view contact info', studentId, width: 100 },
    ];

    return this.renderStudentProfileAccordion(
      HEADING,
      fieldsData,
      FIELD_TYPE.FIELD,
      this.renderLinkOnlyColumn,
    );
  };

  renderIdentifiers = studentProfileInfo => {
    const {
      is_demo: isDemo,
      student_type: studentType,
      is_region_policy_offender: isRegionPolicyOffender,
    } = studentProfileInfo;
    const { YES, NO } = IDENTIFIER_VALUES || {};
    const { HEADING, TEST_ACCOUNT, TRAINING_ACCOUNT, REMOVE_REGION_BANNER } =
      IDENTIFIERS_LABELS || {};

    const fieldsData = [
      [
        {
          label: TEST_ACCOUNT,
          value: isDemo ? YES : NO,
          width: 8,
        },
        {
          label: TRAINING_ACCOUNT,
          value: studentType === STUDENT_TYPE ? NO : YES,
          width: 8,
        },
        {
          label: REMOVE_REGION_BANNER,
          value: isRegionPolicyOffender ? YES : NO,
          width: 8,
        },
      ],
    ];

    return this.renderStudentProfileAccordion(
      HEADING,
      fieldsData,
      FIELD_TYPE.FIELD,
      this.renderColumnContainersInGrid,
    );
  };

  renderActions = studentProfileInfo => {
    const {
      HEADING,
      EDIT_LEAP_USERNAME,
      EDIT_STUDENT_DETAILS,
      ASSIGN_REGION,
      EDIT_PHONE_NO,
    } = ACTIONS_LABELS || {};
    const fieldsData = [
      [
        {
          label: EDIT_LEAP_USERNAME,
          value: () => this.openEditModel(MODAL_FORM.EDIT_STUDENT_USERNAME, false),
          width: 8,
        },
      ],
      [
        {
          label: EDIT_STUDENT_DETAILS,
          value: () =>
            this.openStudentForm({
              formType: FORM_ACTIONS.EDIT,
              data: studentProfileInfo,
            }),
          width: 8,
        },
        {
          label: ASSIGN_REGION,
          value: () => this.openEditCredsModal(MODAL_FORM.EDIT_STUDENT_REGION),
          width: 8,
        },
      ],
      [
        {
          label: EDIT_PHONE_NO,
          value: () => this.openEditModel(MODAL_FORM.EDIT_STUDENT_PHONE, true),
          width: 8,
        },
      ],
    ];

    return this.renderStudentProfileAccordion(
      HEADING,
      fieldsData,
      FIELD_TYPE.LINK,
      this.renderColumnContainersInGrid,
    );
  };

  render() {
    const { studentProfileInfo = {} } = this.props;
    return (
      <React.Fragment>
        {this.renderStudentDemograpicDetails(studentProfileInfo)}
        {this.renderContactDetails(studentProfileInfo)}
        {this.renderIdentifiers(studentProfileInfo)}
        {this.renderActions(studentProfileInfo)}
      </React.Fragment>
    );
  }
}
export default connect(null, dispatch => ({
  actions: bindActionCreators(
    {
      openModal,
      closeModal,
    },
    dispatch,
  ),
}))(StudentProfile);
